import { useState, useEffect, useRef, lazy, Suspense, type ComponentType, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { HomePage } from './components/HomePage'
import { Btn } from './components/Btn'
import { Logo } from './components/Logo'
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react'

// Icônes de marques (retirées de lucide-react) — SVG inline
const LinkedinIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
  </svg>
)
const FacebookIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.03 4.39 11.03 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
  </svg>
)
import { Analytics } from '@vercel/analytics/react'
import { siteConfig } from './config'
import { API_URL } from './services/authService'
import { SiteImagesProvider } from './context/SiteImagesContext'
import { initGoogleAnalytics, trackPageView } from './src/lib/googleAnalytics'
import { NewsletterWidget } from './components/NewsletterWidget'
import { NewsletterModal } from './components/NewsletterModal'
import { WhatsAppButton } from './components/WhatsAppButton'
import { SEO } from './components/SEO'

// Table page → URL, partagée entre la synchro d'URL et les liens de navigation.
// Permet d'avoir de vrais <a href> (nouvel onglet, SEO) tout en gardant la navigation SPA.
const PAGE_PATHS: Record<string, string> = {
  'home': '/',
  'about': '/a-propos',
  'services': '/services',
  'support-operationnel': '/services/support-operationnel',
  'recrutement-entreprises': '/services/recrutement-entreprises',
  'assistance-rh-pme': '/services/assistance-rh-pme',
  'maintenance-informatique-pme': '/services/maintenance-informatique-pme',
  'creation-site-web-pme': '/services/creation-site-web-pme',
  'services-aux-entreprises': '/services/services-aux-entreprises',
  'prestations-specialisees': '/services/prestations-specialisees',
  'pip': '/pip-jepg',
  'blog': '/blog',
  'annonces': '/annonces',
  'contact': '/contact',
  'devis': '/devis',
  'recruter': '/recruter',
  'diagnostic': '/diagnostic',
  'candidater': '/candidater',
  'devenir-partenaire': '/devenir-partenaire',
  'mentions-legales': '/mentions-legales',
  'politique-confidentialite': '/politique-de-confidentialite',
  'admin-dashboard': '/admin-dashboard',
}
const pathForPage = (page: string) => PAGE_PATHS[page] ?? `/${page}`

const lazyComponent = <TModule extends Record<string, unknown>, TName extends keyof TModule>(
  loader: () => Promise<TModule>,
  exportName: TName,
) =>
  lazy(() =>
    loader().then((module) => ({
      default: module[exportName] as ComponentType<any>,
    })),
  )

const AboutPage = lazyComponent(() => import('./components/AboutPage'), 'AboutPage')
const ServicesPage = lazyComponent(() => import('./components/ServicesPage'), 'ServicesPage')
const PIPJEPGPage = lazyComponent(() => import('./components/PIPJEPGPage'), 'PIPJEPGPage')
const BlogPage = lazyComponent(() => import('./components/BlogPage'), 'BlogPage')
const BlogArticlePage = lazyComponent(() => import('./components/BlogArticlePage'), 'BlogArticlePage')
const AnnoncesPage = lazyComponent(() => import('./components/AnnoncesPage'), 'AnnoncesPage')
const AnnonceDetailPage = lazyComponent(() => import('./components/AnnonceDetailPage'), 'AnnonceDetailPage')
const ContactPage = lazyComponent(() => import('./components/ContactPage'), 'ContactPage')
const DemanderDevisPage = lazyComponent(() => import('./components/DemanderDevisPage'), 'DemanderDevisPage')
const CandidaterPage = lazyComponent(() => import('./components/CandidaterPage'), 'CandidaterPage')
const DevenirPartenairePage = lazyComponent(() => import('./components/DevenirPartenairePage'), 'DevenirPartenairePage')
const SupportOperationnelPage = lazyComponent(() => import('./components/SupportOperationnelPage'), 'SupportOperationnelPage')
const RecrutementEntreprisesPage = lazyComponent(() => import('./components/RecrutementEntreprisesPage'), 'RecrutementEntreprisesPage')
const AssistanceRHPMEPage = lazyComponent(() => import('./components/AssistanceRHPMEPage'), 'AssistanceRHPMEPage')
const MaintenanceInformatiquePage = lazyComponent(() => import('./components/MaintenanceInformatiquePage'), 'MaintenanceInformatiquePage')
const CreationSiteWebPage = lazyComponent(() => import('./components/CreationSiteWebPage'), 'CreationSiteWebPage')
const ServicesAuxEntreprisesPage = lazyComponent(() => import('./components/ServicesAuxEntreprisesPage'), 'ServicesAuxEntreprisesPage')
const PrestationsSpecialiseesPage = lazyComponent(() => import('./components/PrestationsSpecialiseesPage'), 'PrestationsSpecialiseesPage')
const DemandeRecrutementPage = lazyComponent(() => import('./components/DemandeRecrutementPage'), 'DemandeRecrutementPage')
const DiagnosticPage = lazyComponent(() => import('./components/DiagnosticPage'), 'DiagnosticPage')
const MentionsLegalesPage = lazyComponent(() => import('./components/MentionsLegalesPage'), 'MentionsLegalesPage')
const PolitiqueConfidentialitePage = lazyComponent(() => import('./components/PolitiqueConfidentialitePage'), 'PolitiqueConfidentialitePage')
const AdminDashboard = lazyComponent(() => import('./components/AdminDashboard'), 'AdminDashboard')
const AdminAuth = lazyComponent(() => import('./components/AdminAuth'), 'AdminAuth')
const NotFoundPage = lazyComponent(() => import('./components/NotFoundPage'), 'NotFoundPage')

export default function Component() {
  // Initialiser la page et le slug depuis l'URL
  const getInitialRoute = () => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname
      const search = window.location.search
      const urlParams = new URLSearchParams(window.location.search)
      
      // 1. Rétrocompatibilité avec les anciens liens (?page=...)
      const oldPage = urlParams.get('page')
      if (oldPage) {
        const oldArticle = urlParams.get('article') || urlParams.get('articleId')
        return { page: oldPage, slug: oldArticle }
      }
      
      // 2. Connexion admin
      if (search && !search.includes('=')) {
        return { page: 'admin', slug: null }
      }
      
      // 3. Nouvelles URLs propres (Axe 1 - SEO)
      if (path === '/' || path === '') return { page: 'home', slug: null }
      if (path === '/a-propos' || path === '/about') return { page: 'about', slug: null }
      if (path === '/services') return { page: 'services', slug: null }
      if (path === '/services/support-operationnel' || path === '/support-operationnel') return { page: 'support-operationnel', slug: null }
      if (path === '/services/recrutement-entreprises') return { page: 'recrutement-entreprises', slug: null }
      if (path === '/services/assistance-rh-pme') return { page: 'assistance-rh-pme', slug: null }
      if (path === '/services/maintenance-informatique-pme') return { page: 'maintenance-informatique-pme', slug: null }
      if (path === '/services/creation-site-web-pme') return { page: 'creation-site-web-pme', slug: null }
      if (path === '/services/services-aux-entreprises') return { page: 'services-aux-entreprises', slug: null }
      if (path === '/services/prestations-specialisees') return { page: 'prestations-specialisees', slug: null }
      if (path === '/pip-jepg' || path === '/pip') return { page: 'pip', slug: null }
      if (path === '/contact') return { page: 'contact', slug: null }
      if (path === '/devis') return { page: 'devis', slug: null }
      if (path === '/recruter') return { page: 'recruter', slug: null }
      if (path === '/diagnostic') return { page: 'diagnostic', slug: null }
      if (path === '/candidater') return { page: 'candidater', slug: null }
      if (path === '/devenir-partenaire') return { page: 'devenir-partenaire', slug: null }
      if (path === '/mentions-legales') return { page: 'mentions-legales', slug: null }
      if (path === '/politique-de-confidentialite') return { page: 'politique-confidentialite', slug: null }
      if (path === '/admin') return { page: 'admin', slug: null }
      if (path === '/admin-dashboard') return { page: 'admin-dashboard', slug: null }
      
      // Routage dynamique pour le Blog
      if (path.startsWith('/blog')) {
        const parts = path.split('/').filter(Boolean)
        if (parts.length === 1) return { page: 'blog', slug: null }
        if (parts.length === 2) return { page: 'article', slug: parts[1] }
      }

      // Routage dynamique pour les Annonces
      if (path.startsWith('/annonces')) {
        const parts = path.split('/').filter(Boolean)
        if (parts.length === 1) return { page: 'annonces', slug: null }
        if (parts.length === 2) return { page: 'annonce', slug: parts[1] }
      }

      return { page: '404', slug: null }
    }
    return { page: 'home', slug: null }
  }

  const initialRoute = getInitialRoute()
  const [currentPage, setCurrentPage] = useState(initialRoute.page)
  const [currentArticleSlug, setCurrentArticleSlug] = useState<string | null>(initialRoute.slug)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false)
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false)
  const servicesMenuRef = useRef<HTMLDivElement>(null)

  // Les 4 pôles affichés dans le menu déroulant Services
  const servicePoles = [
    { label: 'RH & Recrutement', page: 'recrutement-entreprises', desc: 'Recrutement, assistance RH, conseil' },
    { label: 'Services aux entreprises', page: 'services-aux-entreprises', desc: 'Administratif, fournitures, services généraux' },
    { label: 'Digital & IT', page: 'maintenance-informatique-pme', desc: 'Support & maintenance, réseau, sites web et applications' },
    { label: 'Prestations spécialisées', page: 'prestations-specialisees', desc: 'Agriculture, mécanique, logistique, missions' },
  ]

  useEffect(() => {
    initGoogleAnalytics()
  }, [])

  // Réglages du site (validation des pages légales par la direction)
  const [siteSettings, setSiteSettings] = useState<Record<string, string> | null>(null)
  useEffect(() => {
    fetch(`${API_URL}/site-settings`)
      .then((r) => (r.ok ? r.json() : {}))
      .then(setSiteSettings)
      .catch(() => setSiteSettings({}))
  }, [])
  const legalMentionsEnabled = siteSettings?.['legal_mentions_active'] === '1'
  const legalPrivacyEnabled = siteSettings?.['legal_privacy_active'] === '1'

  // Gérer le bouton "Retour" et "Suivant" du navigateur
  useEffect(() => {
    const handlePopState = () => {
      const route = getInitialRoute()
      setCurrentPage(route.page)
      setCurrentArticleSlug(route.slug)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Fermer le menu déroulant Services au clic extérieur ou à la touche Échap
  useEffect(() => {
    if (!servicesMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (servicesMenuRef.current && !servicesMenuRef.current.contains(e.target as Node)) {
        setServicesMenuOpen(false)
      }
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setServicesMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [servicesMenuOpen])

  // Synchroniser l'URL avec la page actuelle
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Ne pas modifier l'URL pour la page admin (on doit préserver le nom d'utilisateur)
      if (currentPage === 'admin') {
        return
      }
      
      let newUrl = '/'
      switch (currentPage) {
        case 'article': newUrl = currentArticleSlug ? `/blog/${currentArticleSlug}` : '/blog'; break
        case 'annonce': newUrl = currentArticleSlug ? `/annonces/${currentArticleSlug}` : '/annonces'; break
        case '404': return  // Conserver l'URL originale invalide
        default: newUrl = pathForPage(currentPage)
      }

      // Éviter de push la même URL si on est déjà dessus (y compris le query string)
      if (window.location.pathname !== newUrl || window.location.search !== '') {
        window.history.pushState({}, '', newUrl)
      }
      if (currentPage !== 'admin-dashboard') {
        trackPageView(newUrl)
      }
    }
  }, [currentPage, currentArticleSlug])

  const navigateTo = (page: string, articleSlug?: string) => {
    setCurrentPage(page)
    if (articleSlug) {
      setCurrentArticleSlug(articleSlug)
    }
    setMobileMenuOpen(false)
    setMobileServicesOpen(false)
    // 'instant' pour neutraliser le scroll-behavior: smooth global,
    // sinon la remontée est animée pendant la transition de page
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  // Lien de navigation interne : vrai <a href> (ouvrable dans un nouvel onglet,
  // suivi par les crawlers) avec navigation SPA au clic simple.
  const NavLink = ({
    page,
    className,
    onClick,
    role,
    children,
    'aria-label': ariaLabel,
  }: {
    page: string
    className?: string
    onClick?: () => void
    role?: string
    children: ReactNode
    'aria-label'?: string
  }) => (
    <a
      href={pathForPage(page)}
      role={role}
      aria-label={ariaLabel}
      aria-current={currentPage === page ? 'page' : undefined}
      className={className}
      onClick={(e) => {
        // Laisser le navigateur gérer nouvel onglet / nouvelle fenêtre
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
        e.preventDefault()
        onClick?.()
        navigateTo(page)
      }}
    >
      {children}
    </a>
  )

  // Liste partagée des pages de services (affichée dans le sous-menu)
  const serviceChildren = [
    { id: 'recrutement-entreprises', label: 'Recrutement entreprises' },
    { id: 'assistance-rh-pme', label: 'Assistance RH PME' },
    { id: 'services-aux-entreprises', label: 'Services aux entreprises' },
    { id: 'maintenance-informatique-pme', label: 'Support et maintenance informatique' },
    { id: 'creation-site-web-pme', label: 'Sites web & applications' },
    { id: 'prestations-specialisees', label: 'Prestations spécialisées' },
    { id: 'support-operationnel', label: 'Support opérationnel' },
  ]

  // Structure des pages parent-enfant
  const serviceParent = { parent: 'services', children: serviceChildren }
  const pageHierarchy: Record<string, { parent: string; children: { id: string; label: string }[] }> = {
    'services': serviceParent,
    'support-operationnel': serviceParent,
    'recrutement-entreprises': serviceParent,
    'assistance-rh-pme': serviceParent,
    'maintenance-informatique-pme': serviceParent,
    'creation-site-web-pme': serviceParent,
    'services-aux-entreprises': serviceParent,
    'prestations-specialisees': serviceParent,
    'blog': { parent: 'blog', children: [] },
    'article': { parent: 'blog', children: [] },
    'annonces': { parent: 'annonces', children: [] },
    'annonce': { parent: 'annonces', children: [] },
  }

  // Détermine la page parente de la page actuelle
  const getParentPage = (page: string): string => {
    return pageHierarchy[page]?.parent || page
  }

  // Vérifie si on doit afficher le sous-menu
  const shouldShowSubmenu = (): boolean => {
    const hierarchy = pageHierarchy[currentPage]
    return hierarchy !== undefined && hierarchy.children.length > 0 && hierarchy.parent !== currentPage
  }

  // Récupère les enfants de la page parente
  const getSubmenuItems = () => {
    const hierarchy = pageHierarchy[currentPage]
    return hierarchy?.children || []
  }

  const parentPage = getParentPage(currentPage)

  return (
    <SiteImagesProvider>
    <div className="min-h-screen bg-white">
      {/* Lien d'évitement pour la navigation clavier / lecteurs d'écran */}
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[100] focus:bg-white focus:text-jepg-blue focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg"
      >
        Aller au contenu
      </a>
      {/* Vercel Web Analytics (Pistage invisible et respectueux de la vie privée) */}
      <Analytics />
      {(currentPage === 'admin' || currentPage === 'admin-dashboard') && (
        <SEO
          title="Administration"
          description="Espace d'administration JE Pro Group."
          noindex
        />
      )}
      <NewsletterModal
        currentPage={currentPage}
        excludePages={['admin', 'admin-dashboard', 'contact', 'devis', 'candidater', 'devenir-partenaire']}
      />

      {/* Navigation - masquée pour le dashboard admin */}
      {currentPage !== 'admin-dashboard' && currentPage !== 'admin' && (
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <NavLink page="home" className="flex-shrink-0" aria-label="JE Pro Group - Accueil">
              <Logo size={50} />
            </NavLink>

            {/* Desktop Navigation — à partir de 1280 px : en dessous, les libellés
                passaient sur deux lignes (« À propos », « PIP JEPG », « CV Gen »). */}
            <div className="hidden xl:flex items-center space-x-8 whitespace-nowrap">
              <NavLink page="home" className={`transition-colors hover:text-jepg-blue ${currentPage === 'home' ? 'text-jepg-blue' : 'text-gray-700'}`}>Accueil</NavLink>
              <NavLink page="about" className={`transition-colors hover:text-jepg-blue ${currentPage === 'about' ? 'text-jepg-blue' : 'text-gray-700'}`}>À propos</NavLink>
              <div
                className="relative"
                ref={servicesMenuRef}
                onMouseEnter={() => setServicesMenuOpen(true)}
                onMouseLeave={() => setServicesMenuOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setServicesMenuOpen((v) => !v)}
                  aria-haspopup="true"
                  aria-expanded={servicesMenuOpen}
                  className={`flex items-center gap-1 transition-colors hover:text-jepg-blue ${parentPage === 'services' ? 'text-jepg-blue' : 'text-gray-700'}`}
                >
                  Services <ChevronDown size={16} className={`transition-transform ${servicesMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {servicesMenuOpen && (
                  <div className="absolute left-0 top-full pt-3 z-50">
                    <div className="w-72 bg-white rounded-xl shadow-xl border border-gray-100 py-2" role="menu">
                      {servicePoles.map((pole) => (
                        <NavLink
                          key={pole.page}
                          page={pole.page}
                          role="menuitem"
                          onClick={() => setServicesMenuOpen(false)}
                          className="block px-4 py-2.5 hover:bg-jepg-blue-light transition-colors"
                        >
                          <span className="block font-medium text-gray-900">{pole.label}</span>
                          <span className="block text-xs text-gray-500">{pole.desc}</span>
                        </NavLink>
                      ))}
                      <div className="mt-1 pt-1 border-t border-gray-100">
                        <NavLink
                          page="services"
                          role="menuitem"
                          onClick={() => setServicesMenuOpen(false)}
                          className="px-4 py-2.5 text-sm font-medium text-jepg-blue hover:bg-jepg-blue-light transition-colors flex items-center gap-1"
                        >
                          Voir tous les services <ChevronRight size={14} />
                        </NavLink>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <NavLink page="pip" className={`transition-colors hover:text-jepg-blue ${currentPage === 'pip' ? 'text-jepg-blue' : 'text-gray-700'}`}>PIP JEPG</NavLink>
              <NavLink page="annonces" className={`transition-colors hover:text-jepg-blue ${parentPage === 'annonces' ? 'text-jepg-blue' : 'text-gray-700'}`}>Annonces</NavLink>
              <NavLink page="blog" className={`transition-colors hover:text-jepg-blue ${parentPage === 'blog' ? 'text-jepg-blue' : 'text-gray-700'}`}>Blog</NavLink>
              {/* CV Gen : app séparée servie sous /cv (réécriture Vercel) — vrai <a>, pas une NavLink de page SPA interne */}
              <a href="/cv" className="transition-colors hover:text-jepg-blue text-gray-700">CV Gen</a>
              <NavLink page="contact" className={`transition-colors hover:text-jepg-blue ${currentPage === 'contact' ? 'text-jepg-blue' : 'text-gray-700'}`}>Contact</NavLink>
              <Btn variant="secondary" size="md" onClick={() => navigateTo('devis')}>Demander un devis</Btn>
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="xl:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.22, ease: 'easeInOut' }}
                className="xl:hidden overflow-hidden border-t border-gray-200"
              >
                {/* max-h + overflow : le menu reste utilisable même quand son contenu
                    dépasse la hauteur de l'écran (accordéon Services ouvert) */}
                <div className="flex flex-col space-y-3 py-4 max-h-[calc(100vh-6rem)] overflow-y-auto">
                  <NavLink page="home" className={`block px-4 py-2 rounded-lg transition-colors ${currentPage === 'home' ? 'bg-jepg-blue-light text-jepg-blue' : 'text-gray-700 hover:bg-gray-50'}`}>Accueil</NavLink>
                  <NavLink page="about" className={`block px-4 py-2 rounded-lg transition-colors ${currentPage === 'about' ? 'bg-jepg-blue-light text-jepg-blue' : 'text-gray-700 hover:bg-gray-50'}`}>À propos</NavLink>

                  {/* Services - accordéon */}
                  <button
                    type="button"
                    onClick={() => setMobileServicesOpen((v) => !v)}
                    className={`flex items-center justify-between px-4 py-2 rounded-lg transition-colors ${parentPage === 'services' ? 'bg-jepg-blue-light text-jepg-blue' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    Services
                    <ChevronDown size={18} className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileServicesOpen && (
                    <div className="flex flex-col space-y-1 pl-4">
                      {servicePoles.map((pole) => (
                        <NavLink
                          key={pole.page}
                          page={pole.page}
                          className="block px-4 py-2 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {pole.label}
                        </NavLink>
                      ))}
                      <NavLink
                        page="services"
                        className="block px-4 py-2 rounded-lg text-sm font-medium text-jepg-blue hover:bg-jepg-blue-light transition-colors"
                      >
                        Voir tous les services
                      </NavLink>
                    </div>
                  )}

                  <NavLink page="pip" className={`block px-4 py-2 rounded-lg transition-colors ${currentPage === 'pip' ? 'bg-jepg-blue-light text-jepg-blue' : 'text-gray-700 hover:bg-gray-50'}`}>PIP JEPG</NavLink>
                  <NavLink page="annonces" className={`block px-4 py-2 rounded-lg transition-colors ${currentPage === 'annonces' ? 'bg-jepg-blue-light text-jepg-blue' : 'text-gray-700 hover:bg-gray-50'}`}>Annonces</NavLink>
                  <NavLink page="blog" className={`block px-4 py-2 rounded-lg transition-colors ${currentPage === 'blog' ? 'bg-jepg-blue-light text-jepg-blue' : 'text-gray-700 hover:bg-gray-50'}`}>Blog</NavLink>
                  <a href="/cv" className="block px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">CV Gen</a>
                  <NavLink page="contact" className={`block px-4 py-2 rounded-lg transition-colors ${currentPage === 'contact' ? 'bg-jepg-blue-light text-jepg-blue' : 'text-gray-700 hover:bg-gray-50'}`}>Contact</NavLink>
                  <Btn variant="secondary" size="md" onClick={() => navigateTo('devis')} className="mx-4 justify-start">Demander un devis</Btn>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>
      )}

      {/* Sous-menu (affiché uniquement pour les pages enfants) */}
      {shouldShowSubmenu() && (
        <div className="bg-gray-50 border-b border-gray-200 sticky top-20 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center space-x-1 overflow-x-auto py-3">
              {getSubmenuItems().map((item) => (
                <NavLink
                  key={item.id}
                  page={item.id}
                  className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${currentPage === item.id ? 'bg-jepg-blue text-white' : 'text-gray-600 hover:bg-jepg-blue-light hover:text-jepg-blue'}`}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      <AnimatePresence mode="wait">
        <motion.main
          id="contenu"
          key={currentPage + (currentArticleSlug ?? '')}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.22, ease: 'easeInOut' }}
        >
          <Suspense fallback={<div className="min-h-screen bg-white" />}>
          {currentPage === 'about' ? (
            <AboutPage onNavigate={navigateTo} />
          ) : currentPage === 'services' ? (
            <ServicesPage onNavigate={navigateTo} />
          ) : currentPage === 'pip' ? (
            <PIPJEPGPage onNavigate={navigateTo} />
          ) : currentPage === 'blog' ? (
            <BlogPage onOpenArticle={(articleSlug: string) => navigateTo('article', articleSlug)} />
          ) : currentPage === 'article' && currentArticleSlug ? (
            <BlogArticlePage
              articleSlug={currentArticleSlug}
              onNavigate={navigateTo}
            />
          ) : currentPage === 'annonces' ? (
            <AnnoncesPage onOpenAnnonce={(annonceSlug: string) => navigateTo('annonce', annonceSlug)} />
          ) : currentPage === 'annonce' && currentArticleSlug ? (
            <AnnonceDetailPage
              annonceSlug={currentArticleSlug}
              onNavigate={navigateTo}
            />
          ) : currentPage === 'contact' ? (
            <ContactPage />
          ) : currentPage === 'devis' ? (
            <DemanderDevisPage />
          ) : currentPage === 'recruter' ? (
            <DemandeRecrutementPage />
          ) : currentPage === 'diagnostic' ? (
            <DiagnosticPage />
          ) : currentPage === 'candidater' ? (
            <CandidaterPage />
          ) : currentPage === 'devenir-partenaire' ? (
            <DevenirPartenairePage />
          ) : currentPage === 'mentions-legales' ? (
            siteSettings === null ? <div className="min-h-screen bg-white" />
              : legalMentionsEnabled ? <MentionsLegalesPage onNavigate={navigateTo} />
              : <NotFoundPage onNavigate={navigateTo} />
          ) : currentPage === 'politique-confidentialite' ? (
            siteSettings === null ? <div className="min-h-screen bg-white" />
              : legalPrivacyEnabled ? <PolitiqueConfidentialitePage onNavigate={navigateTo} />
              : <NotFoundPage onNavigate={navigateTo} />
          ) : currentPage === 'support-operationnel' ? (
            <SupportOperationnelPage onNavigate={navigateTo} />
          ) : currentPage === 'recrutement-entreprises' ? (
            <RecrutementEntreprisesPage onNavigate={navigateTo} />
          ) : currentPage === 'assistance-rh-pme' ? (
            <AssistanceRHPMEPage onNavigate={navigateTo} />
          ) : currentPage === 'maintenance-informatique-pme' ? (
            <MaintenanceInformatiquePage onNavigate={navigateTo} />
          ) : currentPage === 'creation-site-web-pme' ? (
            <CreationSiteWebPage onNavigate={navigateTo} />
          ) : currentPage === 'services-aux-entreprises' ? (
            <ServicesAuxEntreprisesPage onNavigate={navigateTo} />
          ) : currentPage === 'prestations-specialisees' ? (
            <PrestationsSpecialiseesPage onNavigate={navigateTo} />
          ) : currentPage === 'admin-dashboard' ? (
            <AdminDashboard onNavigate={navigateTo} />
          ) : currentPage === 'admin' ? (
            <AdminAuth onNavigate={navigateTo} />
          ) : currentPage === '404' ? (
            <NotFoundPage onNavigate={navigateTo} />
          ) : (
            <HomePage onNavigate={navigateTo} />
          )}
          </Suspense>
        </motion.main>
      </AnimatePresence>

      {/* Footer - masqué pour le dashboard admin */}
      {currentPage !== 'admin-dashboard' && currentPage !== 'admin' && (
      <footer className="bg-gray-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Newsletter strip */}
          <div className="bg-jepg-blue-hover rounded-2xl px-8 py-8 mb-12 flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-white mb-1">Restez informé</h3>
              <p className="text-blue-100 text-sm">Conseils RH, actualités et nouveaux articles directement dans votre boîte mail.</p>
            </div>
            <div className="flex-1 min-w-0">
              <NewsletterWidget variant="onDark" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Colonne 1 - À propos */}
            <div>
              <div className="mb-4">
                <Logo size={48} />
              </div>
              <p className="text-gray-400 mb-4">
                Votre partenaire RH et gestion administrative au Cameroun depuis 2021.
              </p>
              <div className="text-gray-400">
                <p>{siteConfig.address}</p>
                <p>{siteConfig.email}</p>
                <p>{siteConfig.phone}</p>
              </div>
              <div className="flex gap-3 mt-5">
                <a href={siteConfig.socials.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
                  className="w-10 h-10 rounded-lg bg-gray-800 hover:bg-jepg-blue flex items-center justify-center transition-colors">
                  <LinkedinIcon size={18} />
                </a>
                <a href={siteConfig.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                  className="w-10 h-10 rounded-lg bg-gray-800 hover:bg-jepg-blue flex items-center justify-center transition-colors">
                  <FacebookIcon size={18} />
                </a>
              </div>
            </div>

            {/* Colonne 2 - Services */}
            <div>
              <h3 className="text-lg mb-4">Nos Services</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <NavLink page="recrutement-entreprises" className="hover:text-white transition-colors">Recrutement entreprises</NavLink>
                </li>
                <li>
                  <NavLink page="assistance-rh-pme" className="hover:text-white transition-colors">Assistance RH PME</NavLink>
                </li>
                <li>
                  <NavLink page="services-aux-entreprises" className="hover:text-white transition-colors">Services aux entreprises</NavLink>
                </li>
                <li>
                  <NavLink page="maintenance-informatique-pme" className="hover:text-white transition-colors">Support et maintenance informatique</NavLink>
                </li>
                <li>
                  <NavLink page="creation-site-web-pme" className="hover:text-white transition-colors">Sites web & applications</NavLink>
                </li>
                <li>
                  <NavLink page="pip" className="hover:text-white transition-colors">PIP JEPG</NavLink>
                </li>
              </ul>
            </div>

            {/* Colonne 3 - À propos */}
            <div>
              <h3 className="text-lg mb-4">JE Pro Group</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <NavLink page="about" className="hover:text-white transition-colors">Qui sommes-nous</NavLink>
                </li>
                <li>
                  <NavLink page="blog" className="hover:text-white transition-colors">Blog & Actualités</NavLink>
                </li>
                <li>
                  <NavLink page="annonces" className="hover:text-white transition-colors">Annonces & Offres</NavLink>
                </li>
                <li>
                  <NavLink page="contact" className="hover:text-white transition-colors">Nous contacter</NavLink>
                </li>
                <li>
                  <NavLink page="candidater" className="hover:text-white transition-colors">Candidater</NavLink>
                </li>
                <li>
                  <a href="/cv" className="hover:text-white transition-colors">Créer mon CV</a>
                </li>
                <li>
                  <NavLink page="support-operationnel" className="hover:text-white transition-colors text-sm">Support opérationnel</NavLink>
                </li>
              </ul>
            </div>

            {/* Colonne 4 - CTA */}
            <div>
              <h3 className="text-lg mb-4">Passez à l'action</h3>
              <p className="text-gray-400 mb-4">
                Besoin d'un accompagnement RH personnalisé ?
              </p>
              <Btn variant="primary" size="md" onClick={() => navigateTo('devis')} className="w-full mb-3">
                Demander un devis
              </Btn>
              <Btn variant="secondary" size="md" onClick={() => navigateTo('candidater')} className="w-full">
                Candidater au PIP
              </Btn>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-3 text-gray-400">
            <p>&copy; {new Date().getFullYear()} JE Pro Group. Tous droits réservés.</p>
            {(legalMentionsEnabled || legalPrivacyEnabled) && (
              <div className="flex items-center gap-6 text-sm">
                {legalMentionsEnabled && (
                  <NavLink page="mentions-legales" className="hover:text-white transition-colors">Mentions légales</NavLink>
                )}
                {legalPrivacyEnabled && (
                  <NavLink page="politique-confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</NavLink>
                )}
              </div>
            )}
          </div>
        </div>
      </footer>
      )}

      {/* Bouton WhatsApp flottant - masqué pour l'espace admin */}
      {currentPage !== 'admin-dashboard' && currentPage !== 'admin' && (
        <WhatsAppButton />
      )}
    </div>
    </SiteImagesProvider>
  )
}
